import apiClient from '@/lib/api-client';
import type {
  AvailabilityStatus,
  ClubMembershipResponse,
  IssueAccessCodeResponse,
  MemberMatchesResponse,
  MemberPerformancesResponse,
  MemberTraining,
  MemberTrainingsResponse,
  RedeemAccessCodeRequest,
  RedeemAccessCodeResponse,
  UpdateOwnMembershipRequest,
} from '@/types/club-membership';
import type { ClubMemberResponse } from '@/types/club-member';

export const clubMembershipService = {
  /** Dossier du membre connecté : club, rôle, capacités. */
  async getMyMembership(): Promise<ClubMembershipResponse> {
    const response = await apiClient.get<ClubMembershipResponse>('/clubs/membership/me');
    return response.data;
  },

  /** Coordonnées que le membre corrige lui-même. */
  async updateMyMembership(data: UpdateOwnMembershipRequest): Promise<ClubMemberResponse> {
    const response = await apiClient.patch<ClubMemberResponse>('/clubs/membership/me', data);
    return response.data;
  },

  /** Planning : convocations du membre, ou tout le planning s'il encadre. */
  async getMyTrainings(): Promise<MemberTrainingsResponse> {
    const response = await apiClient.get<MemberTrainingsResponse>('/clubs/membership/me/trainings');
    return response.data;
  },

  /** Déclarer sa présence ou son absence à une séance. */
  async setMyAttendance(
    trainingId: string,
    attending: boolean
  ): Promise<{ success: boolean; message: string; data: MemberTraining }> {
    const response = await apiClient.put<{
      success: boolean;
      message: string;
      data: MemberTraining;
    }>(`/clubs/membership/me/trainings/${trainingId}/attendance`, { attending });
    return response.data;
  },

  /** Statistiques individuelles du membre joueur. */
  async getMyPerformances(): Promise<MemberPerformancesResponse> {
    const response = await apiClient.get<MemberPerformancesResponse>(
      '/clubs/membership/me/performances'
    );
    return response.data;
  },

  /** Calendrier du membre : convocations, réponses, résultats. */
  async getMyMatches(): Promise<MemberMatchesResponse> {
    const response = await apiClient.get<MemberMatchesResponse>('/clubs/membership/me/matches');
    return response.data;
  },

  /** Répondre à une convocation. */
  async setMyAvailability(
    matchId: string,
    input: { status: AvailabilityStatus; note?: string }
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.put<{ success: boolean; message: string }>(
      `/clubs/membership/me/matches/${matchId}/availability`,
      input
    );
    return response.data;
  },

  /** Échange du code d'accès contre des identifiants — appelé sans session. */
  async redeemAccessCode(data: RedeemAccessCodeRequest): Promise<RedeemAccessCodeResponse> {
    const response = await apiClient.post<RedeemAccessCodeResponse>(
      '/clubs/membership/redeem',
      data
    );
    return response.data;
  },

  /** Régénérer le code d'un membre — côté club. */
  async issueAccessCode(memberId: string): Promise<IssueAccessCodeResponse> {
    const response = await apiClient.post<IssueAccessCodeResponse>(
      `/clubs/me/members/${memberId}/access-code`
    );
    return response.data;
  },
};
